import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, RefreshCw, ArrowUpDown } from 'lucide-react';
import type { DeploymentRequest, User } from '../../types';
import type { ActionType } from '../../types/deploymentTypes';
import DeploymentCard from './DeploymentCard';

type FilterBy = 'all' | 'pending' | 'approved' | 'deployed' | 'my-requests';
type SortBy = 'newest' | 'oldest' | 'priority-high' | 'priority-low' | 'status-asc' | 'status-desc';

interface DeploymentsListProps {
  title: string;
  deployments: DeploymentRequest[];
  currentUser: User;
  onAction: (action: ActionType, deploymentId: string, description?: string, releaseNote?: string) => Promise<void>;
  filterBy?: FilterBy;
  onRefresh?: () => void;
  // Pre-fills the search box (e.g. from the header's global search). The
  // nonce lets the same text be re-applied after the user has edited it.
  searchPreset?: { text: string; nonce: number } | null;
}

// Modules arrive as {_id, name, branch} objects (or plain strings on legacy
// records) — joining the objects directly produced "[object Object]".
const moduleLabel = (m: unknown): string => {
  if (typeof m === 'string') return m;
  if (m && typeof m === 'object') return String((m as any).name || '');
  return '';
};

const DeploymentsList: React.FC<DeploymentsListProps> = ({ 
  title, 
  deployments, 
  currentUser, 
  onAction,
  filterBy = 'all',
  onRefresh,
  searchPreset
}) => {
  const [search, setSearch] = useState(searchPreset?.text || '');
  useEffect(() => {
    if (searchPreset) setSearch(searchPreset.text);
  }, [searchPreset]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('newest');
  const [showFilters, setShowFilters] = useState(false);
  // Production has ~650 requests and every card used to render at once (each
  // card is heavy). Show a page at a time and let the user ask for more.
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Back to the first page whenever the result set changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, selectedStatuses, selectedPriorities, selectedTypes, selectedModules, sortBy, title]);

  const getFilteredDeployments = (source: DeploymentRequest[]) => {
    // Always work on a copy — .sort() below sorts in place, and this used to
    // reorder the parent's deployments prop array directly.
    let filtered = [...source];

    // Apply role-based filtering
    if (filterBy === 'my-requests') {
      filtered = filtered.filter(d => d.requestedBy && d.requestedBy.id === currentUser.id);
    } else if (filterBy === 'pending' && currentUser.role === 'tl') {
      filtered = filtered.filter(d => d.status === 'pending');
    } else if (filterBy === 'pending' && currentUser.role === 'pm') {
      filtered = filtered.filter(d => d.status === 'tl_approved');
    } else if (filterBy === 'approved' && currentUser.role === 'devops') {
      filtered = filtered.filter(d => d.status === 'pm_approved');
    }

    // Apply search filter
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(d => {
        const moduleNames = (Array.isArray(d.modules)
          ? d.modules.map(moduleLabel).join(' ')
          : moduleLabel(d.module)).toLowerCase();
        return (
          (d.title || '').toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q) ||
          moduleNames.includes(q)
        );
      });
    }

    // Apply status filter (checkbox-based)
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(d => d.status && selectedStatuses.includes(d.status));
    }

    // Apply module filter (checkbox-based)
    if (selectedModules.length > 0) {
      filtered = filtered.filter(d => {
        // Check multi-select modules
        if (Array.isArray(d.modules)) {
          return d.modules.some(m => {
            const moduleName = typeof m === 'object' && m !== null && 'name' in m 
              ? (m as any).name 
              : typeof m === 'string' ? m : '';
            return selectedModules.includes(moduleName);
          });
        }
        // Check legacy single module
        if (d.module) {
          const moduleName = typeof d.module === 'object' && d.module !== null && 'name' in d.module
            ? (d.module as any).name
            : typeof d.module === 'string' ? d.module : '';
          return selectedModules.includes(moduleName);
        }
        return false;
      });
    }

    // Apply priority filter (checkbox-based)
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(d => selectedPriorities.includes(d.priority || 'medium'));
    }

    // Apply deployment type filter (checkbox-based)
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(d => d.deploymentType && selectedTypes.includes(d.deploymentType));
    }

    // Apply sorting
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const statusOrder = { pending: 1, tl_approved: 2, pm_approved: 3, deployed: 4, rejected: 5, rolled_back: 6 };
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          // Most recent first
          const dateA_newest = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_newest = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_newest - dateA_newest;
        
        case 'oldest':
          // Oldest first
          const dateA_oldest = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_oldest = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateA_oldest - dateB_oldest;
        
        case 'priority-high':
          // High priority first
          const priorityA_high = priorityOrder[a.priority || 'medium'];
          const priorityB_high = priorityOrder[b.priority || 'medium'];
          if (priorityA_high !== priorityB_high) {
            return priorityB_high - priorityA_high;
          }
          // Then by newest
          const dateA_ph = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_ph = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_ph - dateA_ph;
        
        case 'priority-low':
          // Low priority first
          const priorityA_low = priorityOrder[a.priority || 'medium'];
          const priorityB_low = priorityOrder[b.priority || 'medium'];
          if (priorityA_low !== priorityB_low) {
            return priorityA_low - priorityB_low;
          }
          // Then by newest
          const dateA_pl = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_pl = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_pl - dateA_pl;
        
        case 'status-asc':
          // Status ascending (pending → deployed)
          const statusA_asc = statusOrder[a.status || 'pending'];
          const statusB_asc = statusOrder[b.status || 'pending'];
          if (statusA_asc !== statusB_asc) {
            return statusA_asc - statusB_asc;
          }
          // Then by newest
          const dateA_sa = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_sa = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_sa - dateA_sa;
        
        case 'status-desc':
          // Status descending (deployed → pending)
          const statusA_desc = statusOrder[a.status || 'pending'];
          const statusB_desc = statusOrder[b.status || 'pending'];
          if (statusA_desc !== statusB_desc) {
            return statusB_desc - statusA_desc;
          }
          // Then by newest
          const dateA_sd = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_sd = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_sd - dateA_sd;
        
        default:
          // Default: newest first
          const dateA_default = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
          const dateB_default = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
          return dateB_default - dateA_default;
      }
    });
  };

  // Map TL and PM approvals to approvalHistory for each deployment
  const mappedDeployments = deployments.map((d) => {
    const approvalHistory: import('../../types').ApprovalHistoryEntry[] = [];
    // TL approvals: show all TLs, mark as Approved or Pending
    if (Array.isArray(d.tlApprovals)) {
      d.tlApprovals.forEach((a: any) => {
        approvalHistory.push({
          name: a.tl?.name || '',
          role: 'TL',
          date: a.approvedAt || '',
          status: a.approved ? 'Approved' : 'Pending',
        });
      });
    }
    // PM approvals: show all PMs, mark as Approved or Pending
    if (Array.isArray(d.pmApprovals)) {
      d.pmApprovals.forEach((a: any) => {
        approvalHistory.push({
          name: a.pm?.name || '',
          role: 'PM',
          date: a.approvedAt || '',
          status: a.approved ? 'Approved' : 'Pending',
        });
      });
    }
    return { ...d, approvalHistory };
  });

  // Use mapped Deployments for filtering and rendering
  const filteredDeployments = getFilteredDeployments(mappedDeployments);
  // Support both legacy single module and new multi-select modules
  const modules = Array.from(new Set(
    deployments.flatMap(d => {
      if (Array.isArray(d.modules)) {
        return d.modules.map(m => {
          if (typeof m === 'object' && m !== null && 'name' in m && typeof (m as any).name === 'string') {
            return (m as any).name;
          }
          if (typeof m === 'string') {
            return m;
          }
          return '';
        });
      } else if (d.module) {
        if (typeof d.module === 'object' && d.module !== null && 'name' in d.module && typeof (d.module as any).name === 'string') {
          return [(d.module as any).name];
        }
        if (typeof d.module === 'string') {
          return [d.module];
        }
      }
      return [];
    })
  )).filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            {filteredDeployments.length} of {deployments.length} requests
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Refresh deployments"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Search and Filter Toggle */}
        <div className="p-4 flex flex-wrap items-center gap-4 border-b border-gray-200">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search deployments..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-semibold transition-all ${
              showFilters 
                ? 'bg-blue-50 border-blue-500 text-blue-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
            }`}
          >
            <Filter className="w-4 h-4" />
            Advanced Filters
            {(selectedStatuses.length + selectedPriorities.length + selectedTypes.length + selectedModules.length) > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {selectedStatuses.length + selectedPriorities.length + selectedTypes.length + selectedModules.length}
              </span>
            )}
          </button>

          {/* Clear All Filters Button */}
          {(selectedStatuses.length + selectedPriorities.length + selectedTypes.length + selectedModules.length) > 0 && (
            <button
              onClick={() => {
                setSelectedStatuses([]);
                setSelectedPriorities([]);
                setSelectedTypes([]);
                setSelectedModules([]);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-red-300 bg-white text-red-600 hover:bg-red-50 hover:border-red-400 font-semibold transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Clear Filters
            </button>
          )}

          {/* Sort dropdown */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="newest">📅 Newest First</option>
              <option value="oldest">📅 Oldest First</option>
              <option value="priority-high">🔴 High Priority First</option>
              <option value="priority-low">🟢 Low Priority First</option>
              <option value="status-asc">📊 Status: Ascending</option>
              <option value="status-desc">📊 Status: Descending</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="p-6 bg-gray-50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Status Filter */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Status</h3>
                <button
                  onClick={() => {
                    const allStatuses = ['pending', 'tl_approved', 'pm_approved', 'deployed', 'rejected'];
                    setSelectedStatuses(selectedStatuses.length === allStatuses.length ? [] : allStatuses);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedStatuses.length === 5 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2">
                {[
                  { value: 'pending', label: 'Pending', color: 'text-yellow-600' },
                  { value: 'tl_approved', label: 'TL Approved', color: 'text-blue-600' },
                  { value: 'pm_approved', label: 'PM Approved', color: 'text-indigo-600' },
                  { value: 'deployed', label: 'Deployed', color: 'text-green-600' },
                  { value: 'rejected', label: 'Rejected', color: 'text-red-600' }
                ].map(status => (
                  <label key={status.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status.value]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.value));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Priority Filter */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Priority</h3>
                <button
                  onClick={() => {
                    const allPriorities = ['high', 'medium', 'low'];
                    setSelectedPriorities(selectedPriorities.length === allPriorities.length ? [] : allPriorities);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedPriorities.length === 3 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2">
                {[
                  { value: 'high', label: '🔴 High', color: 'text-red-600' },
                  { value: 'medium', label: '� Medium', color: 'text-yellow-600' },
                  { value: 'low', label: '� Low', color: 'text-green-600' }
                ].map(priority => (
                  <label key={priority.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedPriorities.includes(priority.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPriorities([...selectedPriorities, priority.value]);
                        } else {
                          setSelectedPriorities(selectedPriorities.filter(p => p !== priority.value));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${priority.color}`}>{priority.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Type Filter */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Type</h3>
                <button
                  onClick={() => {
                    const allTypes = ['bug', 'feature', 'payload', 'version', 'new-deployment', 'db-script'];
                    setSelectedTypes(selectedTypes.length === allTypes.length ? [] : allTypes);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedTypes.length === 6 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2">
                {[
                  { value: 'bug', label: '🐛 Bug' },
                  { value: 'feature', label: '✨ Feature' },
                  { value: 'payload', label: '📦 Payload' },
                  { value: 'version', label: '🔢 Version' },
                  { value: 'new-deployment', label: '🚀 New Deployment' },
                  { value: 'db-script', label: '🗄️ DB Script' }
                ].map(type => (
                  <label key={type.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTypes([...selectedTypes, type.value]);
                        } else {
                          setSelectedTypes(selectedTypes.filter(t => t !== type.value));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Module Filter */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Modules</h3>
                <button
                  onClick={() => {
                    setSelectedModules(selectedModules.length === modules.length ? [] : [...modules]);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedModules.length === modules.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {modules.map(module => {
                  const moduleStr = typeof module === 'string' ? module : module?.name || String(module);
                  return (
                    <label key={moduleStr} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(moduleStr)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModules([...selectedModules, moduleStr]);
                          } else {
                            setSelectedModules(selectedModules.filter(m => m !== moduleStr));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{moduleStr}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="space-y-4">
        {filteredDeployments.slice(0, visibleCount).map((deployment) => (
          <DeploymentCard
            key={deployment.id}
            deployment={deployment}
            currentUser={currentUser}
            onAction={onAction}
            onUpdate={() => {
              // Trigger refresh after update
              if (onRefresh) {
                onRefresh();
              }
            }}
            // Admin has no dedicated approval queues, so All Requests is where
            // their approve/deploy/reject actions have to live.
            showActions={title !== 'All Deployment Requests' || currentUser.role === 'admin'}
            isMinimalView={true}
          />
        ))}

        {filteredDeployments.length > visibleCount && (
          <div className="flex flex-col items-center gap-2 py-4">
            <p className="text-sm text-gray-500">
              Showing {visibleCount} of {filteredDeployments.length} requests
            </p>
            <button
              type="button"
              onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Show {Math.min(PAGE_SIZE, filteredDeployments.length - visibleCount)} more
            </button>
          </div>
        )}


        {filteredDeployments.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No deployments found</h3>
            <p className="text-gray-500">
              {search || selectedStatuses.length > 0 || selectedModules.length > 0 || selectedPriorities.length > 0 || selectedTypes.length > 0
                ? 'Try adjusting your filters to see more results.'
                : 'No deployment requests have been submitted yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentsList;